import { useEffect, useMemo } from "react";
import {
  CircleMarker,
  MapContainer,
  Polygon,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { LatLngBounds } from "leaflet";
import { OutcodeBoundary } from "./outcodeBoundary";

// Google Maps satellite + roads / labels stack. Internal-tool usage;
// the same URL pattern is fine across QA + prod (no referrer lock).
const TILE_URL = "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";

export interface OverviewPin {
  id: string;
  lat: number;
  lon: number;
  label: string;
  // Optional GeoJSON-style coordinate array for the lead's chosen
  // building. 3-deep = Polygon, 4-deep = MultiPolygon (matches the
  // `BuildingCandidate.polygon` shape on the wire). When present we
  // render the polygon footprint instead of just a dot - far more
  // useful for spotting the right building on a satellite tile.
  polygon?: number[][][] | number[][][][] | null;
}

const FitToPins: React.FC<{ pins: OverviewPin[] }> = ({ pins }) => {
  const map = useMap();
  useEffect(() => {
    if (pins.length === 0) return;
    const bounds = new LatLngBounds(pins.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [pins, map]);
  return null;
};

// Convert a `BuildingCandidate.polygon` (Polygon or MultiPolygon
// coordinates) into the leaflet-shaped `LatLng[][]` arrays one
// `<Polygon />` per outer-ring stack. Returns one entry per polygon
// part; each entry is the rings of that polygon (outer + holes).
const buildingPolygonToLeafletRings = (
  poly: number[][][] | number[][][][] | null | undefined
): [number, number][][][] => {
  if (!poly || poly.length === 0) return [];
  const isMulti = Array.isArray((poly as number[][][][])?.[0]?.[0]?.[0]);
  if (isMulti) {
    return (poly as number[][][][]).map((p) => p.map(ringToLatLngs));
  }
  return [(poly as number[][][]).map(ringToLatLngs)];
};

// GeoJSON coords are [[lon, lat], ...]; leaflet wants [[lat, lon],
// ...]. Walks one ring; flips Polygon/MultiPolygon at the call site.
const ringToLatLngs = (ring: number[][]): [number, number][] =>
  ring.map(([lon, lat]) => [lat, lon] as [number, number]);

// Bright orange against the dark Google satellite tiles - the prior
// blue (#0d6efd) was the same colour as the building-pin marker and
// nearly invisible against rooftops.
const SEED_OUTCODE_COLOUR = "#ff6b1a";
const NEIGHBOUR_OUTCODE_COLOUR = "#ffa54a";

const geometryToLeafletRings = (
  geometry: OutcodeBoundary["geometry"] | null | undefined
): [number, number][][][] => {
  // Defensive: an outcode file with malformed / missing coordinates
  // shouldn't crash the whole map. Skip silently and let the operator
  // see the rest of the search area.
  if (!geometry || !Array.isArray(geometry.coordinates)) return [];
  if (geometry.type === "Polygon") {
    return [(geometry.coordinates as number[][][]).map(ringToLatLngs)];
  }
  return (geometry.coordinates as number[][][][]).map((poly) =>
    poly.map(ringToLatLngs)
  );
};

export const LeadsOverviewMap: React.FC<{
  pins: OverviewPin[];
  onPinClick?: (id: string) => void;
  // Seed outcode boundary (e.g. BS11). Rendered with a thicker
  // stroke than the neighbour boundaries so the operator can tell
  // which outcode they typed vs. which were pulled in by the radius.
  outcodeBoundary?: OutcodeBoundary | null;
  // The full expanded outcode set (seed + every neighbour the SQL
  // filter actually queried). Rendered as additional dashed
  // outlines so the operator sees "this is everything we searched".
  // The seed entry is filtered out client-side so we don't
  // double-render it.
  expandedOutcodeBoundaries?: OutcodeBoundary[];
}> = ({
  pins,
  onPinClick,
  outcodeBoundary,
  expandedOutcodeBoundaries,
}) => {
  const fallbackCentre: [number, number] = pins.length
    ? [pins[0].lat, pins[0].lon]
    : [54.5, -2.5]; // GB

  // Convert the outcode polygons into leaflet-shaped rings.
  // `Polygon.positions` accepts `LatLng[][]` (array of rings) for
  // both single-Polygon and MultiPolygon - we render each
  // MultiPolygon part as a separate <Polygon /> so each can have
  // holes if the dataset includes them.
  const seedRings = useMemo<[number, number][][][] | null>(() => {
    if (!outcodeBoundary) return null;
    return geometryToLeafletRings(outcodeBoundary.geometry);
  }, [outcodeBoundary]);

  const neighbourBoundaries = useMemo<OutcodeBoundary[]>(() => {
    if (!expandedOutcodeBoundaries) return [];
    const seedCode = outcodeBoundary?.outcode;
    return expandedOutcodeBoundaries.filter(
      (b) => !seedCode || b.outcode !== seedCode
    );
  }, [expandedOutcodeBoundaries, outcodeBoundary]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <MapContainer
        center={fallbackCentre}
        zoom={pins.length ? 12 : 6}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer attribution="© Google" url={TILE_URL} />
        <FitToPins pins={pins} />
        {neighbourBoundaries.map((b) =>
          geometryToLeafletRings(b.geometry).map((rings, i) => (
            <Polygon
              key={`outcode-neighbour-${b.outcode}-${i}`}
              positions={rings}
              pathOptions={{
                color: NEIGHBOUR_OUTCODE_COLOUR,
                weight: 2,
                opacity: 0.85,
                dashArray: "5 4",
                fillColor: NEIGHBOUR_OUTCODE_COLOUR,
                fillOpacity: 0.04,
              }}
            />
          ))
        )}
        {seedRings?.map((rings, i) => (
          <Polygon
            key={`outcode-seed-${i}`}
            positions={rings}
            pathOptions={{
              color: SEED_OUTCODE_COLOUR,
              weight: 2.5,
              opacity: 0.9,
              dashArray: "6 4",
              fillColor: SEED_OUTCODE_COLOUR,
              fillOpacity: 0.05,
            }}
          />
        ))}
        {pins.map((p) => {
          const rings = buildingPolygonToLeafletRings(p.polygon);
          if (rings.length > 0) {
            // Render every part of a Polygon / MultiPolygon. Each
            // <Polygon> shares the lead id so a click anywhere on
            // the footprint selects the right row.
            return rings.map((part, i) => (
              <Polygon
                key={`lead-${p.id}-${i}`}
                positions={part}
                pathOptions={{
                  color: "#0d6efd",
                  weight: 2,
                  opacity: 0.9,
                  fillColor: "#0d6efd",
                  fillOpacity: 0.35,
                }}
                eventHandlers={{ click: () => onPinClick?.(p.id) }}
              >
                <Tooltip>{p.label}</Tooltip>
              </Polygon>
            ));
          }
          // Fallback to the old dot when the lead has no building
          // polygon (e.g. CSV import with a postcode-only address).
          return (
            <CircleMarker
              key={p.id}
              center={[p.lat, p.lon]}
              radius={6}
              pathOptions={{ color: "#0d6efd", fillOpacity: 0.6, weight: 2 }}
              eventHandlers={{ click: () => onPinClick?.(p.id) }}
            >
              <Tooltip>{p.label}</Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
      {outcodeBoundary && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            left: 4,
            background: "rgba(255,255,255,0.85)",
            padding: "2px 6px",
            borderRadius: 3,
            color: "#666",
            fontSize: 10,
            zIndex: 400,
            pointerEvents: "none",
          }}
        >
          {outcodeBoundary.outcode}
          {neighbourBoundaries.length > 0
            ? ` + ${neighbourBoundaries.length} neighbour${neighbourBoundaries.length === 1 ? "" : "s"}`
            : ""}{" "}
          boundary © Wikipedia contributors, CC-BY-SA 3.0
        </div>
      )}
      {pins.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255,255,255,0.85)",
            padding: 12,
            borderRadius: 4,
            color: "#666",
            fontSize: 12,
          }}
        >
          No locations to show.
        </div>
      )}
    </div>
  );
};

export default LeadsOverviewMap;
